import { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'
import Box from '@mui/material/Box'
import { PERSONAS } from './cohorts'

const MIN_HEIGHT = 320
const MIN_RADIUS = 34
const MAX_RADIUS = 110
const BUBBLE_PADDING = 2
// DVD-screensaver cruising speed (px per tick, ~60 ticks/s) in drift mode.
const CRUISE_SPEED = 0.5
// Bounds the gather speed in center mode.
const MAX_SPEED = 1.2
const HOVER_SCALE = 1.08
// Sparkle ring (a clip capture landing on a persona bubble).
const SPARKLE_RING_MS = 900
const SPARKLE_RING_TRAVEL = 1.35
// How long a quote callout hangs off its bubble before fading.
const CALLOUT_MS = 6000

// Physics radius: hover genuinely inflates the body so a hovered bubble
// shoves its neighbors, not just its rendering.
const physicalR = (node) => node.r * (node.hovered ? HOVER_SCALE : 1)

const personaCounts = (cohorts) =>
  PERSONAS.map(({ key, match }) => ({
    key,
    count: cohorts.filter(match).reduce((sum, c) => sum + c.count, 0),
  }))

// Elastic pairwise collisions (mass ∝ area) so bubbles trade momentum and
// bounce apart instead of d3's forceCollide, which only separates overlap
// and absorbs the relative velocity — that's what made them jitter in a
// clump. Restitution is mode-dependent: 1 in drift (clean deflections),
// low in center mode (bumps absorb so the cluster settles).
const bounceOffEachOther = (nodes, restitution) => {
  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const a = nodes[i]
      const b = nodes[j]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const minDist = physicalR(a) + physicalR(b) + BUBBLE_PADDING
      const dist = Math.hypot(dx, dy) || 1e-6
      if (dist >= minDist) continue

      const nx = dx / dist
      const ny = dy / dist
      const massA = a.r * a.r
      const massB = b.r * b.r

      // Separate proportionally to mass so a small bubble yields to a
      // big one rather than shoving it.
      const overlap = minDist - dist
      a.x -= nx * overlap * (massB / (massA + massB))
      a.y -= ny * overlap * (massB / (massA + massB))
      b.x += nx * overlap * (massA / (massA + massB))
      b.y += ny * overlap * (massA / (massA + massB))

      // Impulse along the collision normal, only if they are closing in.
      const closingSpeed = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny
      if (closingSpeed < 0) {
        const impulse =
          (-(1 + restitution) * closingSpeed) / (1 / massA + 1 / massB)
        a.vx -= (impulse / massA) * nx
        a.vy -= (impulse / massA) * ny
        b.vx += (impulse / massB) * nx
        b.vy += (impulse / massB) * ny
      }
    }
  }
}

const bounceOffWalls = (node, bounds, restitution) => {
  const r = physicalR(node)
  if (node.x < r) {
    node.x = r
    node.vx = Math.abs(node.vx) * restitution
  } else if (node.x > bounds.width - r) {
    node.x = bounds.width - r
    node.vx = -Math.abs(node.vx) * restitution
  }
  if (node.y < r) {
    node.y = r
    node.vy = Math.abs(node.vy) * restitution
  } else if (node.y > bounds.height - r) {
    node.y = bounds.height - r
    node.vy = -Math.abs(node.vy) * restitution
  }

  const speed = Math.hypot(node.vx, node.vy)
  if (speed > MAX_SPEED) {
    node.vx *= MAX_SPEED / speed
    node.vy *= MAX_SPEED / speed
  }
}

// Constant-momentum cruising: collisions and walls set the heading, this
// pins the speed, so motion is steady gliding with clean deflections —
// no random nudges, no decay.
const cruise = (node) => {
  const speed = Math.hypot(node.vx, node.vy)
  if (speed < 1e-6) {
    const angle = Math.random() * 2 * Math.PI
    node.vx = Math.cos(angle) * CRUISE_SPEED
    node.vy = Math.sin(angle) * CRUISE_SPEED
    return
  }
  node.vx = (node.vx / speed) * CRUISE_SPEED
  node.vy = (node.vy / speed) * CRUISE_SPEED
}

// Floating-bubble view of who is recording right now. The d3-force
// simulation is created once and never cools (alphaDecay 0); data
// refreshes only retarget the radii, which ease over via transitions
// instead of resetting the layout. Two modes, switched via the `mode`
// prop: 'drift' is a DVD-screensaver — constant cruise speed, heading
// changed only by elastic bounces — and 'center' kills the momentum and
// gently gathers the bubbles at the middle.
const BubbleField = ({ cohorts, mode = 'center', subscribeSparkles }) => {
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const stateRef = useRef(null)
  const modeRef = useRef(mode)
  // Quote callouts ride their bubble: the tick handler repositions the
  // rendered divs every frame via this element map (React only handles
  // mount/unmount).
  const [callouts, setCallouts] = useState([])
  const calloutElsRef = useRef(new Map())

  useEffect(() => {
    const container = containerRef.current
    // Live-updated by the ResizeObserver below; the tick handler reads it
    // every frame, so the walls track the panel as it resizes.
    const bounds = {
      width: container.offsetWidth,
      height: container.offsetHeight || MIN_HEIGHT,
    }
    const nodes = PERSONAS.map((persona, index) => {
      const angle = Math.random() * 2 * Math.PI
      return {
        ...persona,
        count: 0,
        r: MIN_RADIUS,
        x: (bounds.width * (index + 1)) / (PERSONAS.length + 1),
        y: bounds.height / 2 + (index % 2 === 0 ? -20 : 20),
        vx: Math.cos(angle) * CRUISE_SPEED,
        vy: Math.sin(angle) * CRUISE_SPEED,
      }
    })

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', bounds.width)
      .attr('height', bounds.height)

    const groups = svg
      .selectAll('g.bubble')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'bubble')

    groups
      .append('circle')
      .attr('r', (d) => d.r)
      .style('fill', (d) => d.color)
      .style('fill-opacity', 0.9)
      .style('stroke', 'white')
      .style('stroke-opacity', 0)
      .style('stroke-width', 2.5)

    groups
      .append('text')
      .attr('class', 'count')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.05em')
      .style('fill', 'white')
      .style('font-weight', 700)

    groups
      .append('text')
      .attr('class', 'label')
      .attr('text-anchor', 'middle')
      .style('fill', 'white')
      .style('font-size', '11px')
      .style('font-weight', 600)
      .style('letter-spacing', '1px')
      .text((d) => d.key)

    const tooltip = tooltipRef.current
    groups
      .style('cursor', 'default')
      .on('mouseenter', (event, d) => {
        d.hovered = true
        tooltip.textContent = `${d.key} — ${d.legend}`
        tooltip.style.opacity = 1
        d3.select(event.currentTarget)
          .select('circle')
          .transition()
          .duration(150)
          .attr('r', d.r * HOVER_SCALE)
          .style('fill-opacity', 1)
          .style('stroke', 'white')
          .style('stroke-opacity', 0.9)
          .style('stroke-width', 2.5)
      })
      .on('mousemove', (event) => {
        // Pointer coords come back in visual (post-transform) pixels;
        // divide by the wallboard scale (visual width over layout width)
        // so the tooltip lands in local coordinates.
        const node = containerRef.current
        const scale = node.getBoundingClientRect().width / node.offsetWidth || 1
        const [x, y] = d3.pointer(event, node)
        tooltip.style.left = `${x / scale + 14}px`
        tooltip.style.top = `${y / scale - 10}px`
      })
      .on('mouseleave', (event, d) => {
        d.hovered = false
        tooltip.style.opacity = 0
        d3.select(event.currentTarget)
          .select('circle')
          .transition()
          .duration(150)
          .attr('r', d.r)
          .style('fill-opacity', d.count === 0 ? 0.35 : 0.9)
          .style('stroke-opacity', 0)
      })

    const simulation = d3
      .forceSimulation(nodes)
      .alphaDecay(0)
      .velocityDecay(0)
      .on('tick', () => {
        const drifting = modeRef.current === 'drift'
        bounceOffEachOther(nodes, drifting ? 1 : 0.2)
        nodes.forEach((node) =>
          bounceOffWalls(node, bounds, drifting ? 1 : 0.2)
        )
        if (drifting) nodes.forEach(cruise)
        groups.attr('transform', (d) => `translate(${d.x},${d.y})`)
        // Keep quote callouts pinned above their (moving) bubble.
        // Several on one bubble stack upward by their measured heights
        // (quotes wrap to different line counts) so they never overlap.
        // Deliberately unclamped: a callout may hang past the panel
        // frame rather than squeeze or cover its bubble.
        const stacked = {}
        calloutElsRef.current.forEach((el) => {
          const key = el.dataset.persona
          const node = nodes.find((n) => n.key === key)
          if (!node) return
          const offset = stacked[key] || 0
          stacked[key] = offset + el.offsetHeight + 8
          el.style.left = `${node.x}px`
          el.style.top = `${node.y - physicalR(node) - 14 - offset}px`
        })
      })

    const observer = new ResizeObserver(() => {
      const width = container.offsetWidth
      const height = container.offsetHeight
      if (!width || !height) return
      bounds.width = width
      bounds.height = height
      svg.attr('width', width).attr('height', height)
      // Pull anything the shrink left stranded back inside the walls.
      nodes.forEach((node) => {
        const r = physicalR(node)
        node.x = Math.min(Math.max(node.x, r), width - r)
        node.y = Math.min(Math.max(node.y, r), height - r)
      })
      simulation.force('x')?.x(width / 2)
      simulation.force('y')?.y(height / 2)
    })
    observer.observe(container)

    stateRef.current = { nodes, groups, simulation, bounds }

    return function cleanup() {
      observer.disconnect()
      simulation.stop()
      container.innerHTML = ''
      stateRef.current = null
    }
  }, [])

  useEffect(() => {
    const state = stateRef.current
    if (!state) return
    modeRef.current = mode

    if (mode === 'center') {
      // Damping plus a weak pull (speed-capped by bounceOffWalls) glides
      // the bubbles to the middle, where the absorbing collisions let
      // them come to rest.
      state.simulation
        .velocityDecay(0.12)
        .force('x', d3.forceX(state.bounds.width / 2).strength(0.004))
        .force('y', d3.forceY(state.bounds.height / 2).strength(0.008))
    } else {
      state.simulation.velocityDecay(0).force('x', null).force('y', null)
      // Set adrift again in fresh random directions.
      state.nodes.forEach((node) => {
        const angle = Math.random() * 2 * Math.PI
        node.vx = Math.cos(angle) * CRUISE_SPEED
        node.vy = Math.sin(angle) * CRUISE_SPEED
      })
    }
  }, [mode])

  // Clip events. Create: a radiating ring off the persona's bubble (in
  // step with the blocks and map). Quote: no effect on the circle — the
  // speech-bubble callout itself emanates out of the bubble (see the
  // callout keyframes below).
  useEffect(() => {
    if (!subscribeSparkles) return undefined
    return subscribeSparkles((event) => {
      const state = stateRef.current
      if (!state || !event.personaKey) return
      const group = state.groups.filter((d) => d.key === event.personaKey)
      if (group.empty()) return
      const node = group.datum()

      if (event.type === 'create') {
        group
          .append('circle')
          .attr('class', 'sparkle-ring')
          .attr('r', node.r)
          .style('fill', 'none')
          .style('stroke', 'white')
          .style('stroke-width', 3)
          .style('stroke-opacity', 0.9)
          .style('pointer-events', 'none')
          .transition()
          .duration(SPARKLE_RING_MS)
          .ease(d3.easeCubicOut)
          .attr('r', node.r * SPARKLE_RING_TRAVEL)
          .style('stroke-opacity', 0)
          .remove()
      }

      if (!event.text) return
      const id = `${event.type}-${event.id}`
      setCallouts((old) => [
        // Keep at most 3 on screen; oldest gives way.
        ...old.filter((callout) => callout.id !== id).slice(-2),
        { id, personaKey: event.personaKey, text: event.text },
      ])
      setTimeout(() => {
        setCallouts((old) => old.filter((callout) => callout.id !== id))
      }, CALLOUT_MS)
    })
  }, [subscribeSparkles])

  useEffect(() => {
    const state = stateRef.current
    if (!state || !cohorts) return

    const counts = personaCounts(cohorts)
    const maxCount = Math.max(1, ...counts.map(({ count }) => count))

    state.nodes.forEach((node) => {
      const { count } = counts.find(({ key }) => key === node.key)
      node.count = count
      // Area (r^2) proportional to population; the floor is a clamp (not
      // an additive base) so proportions stay honest above it and tiny or
      // empty groups still stay legible.
      node.targetR = Math.max(
        MIN_RADIUS,
        MAX_RADIUS * Math.sqrt(count / maxCount)
      )
    })

    // Tweening node.r directly keeps the physics body in lockstep with
    // the visual — the collision pass reads node.r live each tick, so a
    // growing bubble shoves its neighbors aside as it expands.
    state.groups
      .select('circle')
      .transition()
      .duration(750)
      .style('fill-opacity', (d) => (d.count === 0 ? 0.35 : 0.9))
      .tween('radius', function tweenRadius(d) {
        const circle = d3.select(this)
        const interpolate = d3.interpolateNumber(d.r, d.targetR)
        return (t) => {
          d.r = interpolate(t)
          circle.attr('r', d.r)
        }
      })

    state.groups
      .select('text.count')
      .transition()
      .duration(750)
      .style('font-size', (d) => `${Math.max(16, d.targetR * 0.45)}px`)
      .tween('text', function tweenCount(d) {
        const node = this
        const from = Number(node.textContent) || 0
        const interpolate = d3.interpolateNumber(from, d.count)
        return (t) => {
          node.textContent = Math.round(interpolate(t))
        }
      })

    state.groups
      .select('text.label')
      .transition()
      .duration(750)
      .attr('dy', (d) => `${Math.max(16, d.targetR * 0.45) * 0.85 + 4}px`)
      .style('fill-opacity', (d) => (d.count === 0 ? 0.8 : 1))

    state.simulation.alpha(0.5).restart()
  }, [cohorts])

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: MIN_HEIGHT }}>
      <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
      {callouts.map(({ id, personaKey, text }) => (
        <Box
          key={id}
          ref={(el) => {
            if (el) {
              el.dataset.persona = personaKey
              calloutElsRef.current.set(id, el)
            } else {
              calloutElsRef.current.delete(id)
            }
          }}
          sx={{
            position: 'absolute',
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            // max-content sidesteps absolute positioning's shrink-to-fit:
            // near the right wall the leftover width would otherwise
            // squeeze the text to a word per line.
            width: 'max-content',
            maxWidth: 220,
            bgcolor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            px: 1.5,
            py: 0.75,
            borderRadius: 3,
            fontSize: 12,
            fontStyle: 'italic',
            textAlign: 'center',
            zIndex: 2,
            // Grows out of the bubble: scaled around the tail tip, with
            // a slight overshoot before settling (percentages of the
            // 6s life: entrance ≈ first 400ms).
            transformOrigin: '50% 100%',
            animation: `bubble-callout ${CALLOUT_MS}ms ease forwards`,
            // Speech-bubble tail, pointing down at the talking bubble.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: '8px solid rgba(0, 0, 0, 0.85)',
            },
            '@keyframes bubble-callout': {
              '0%': {
                opacity: 0,
                transform: 'translate(-50%, -100%) scale(0.2)',
              },
              '5%': {
                opacity: 1,
                transform: 'translate(-50%, -100%) scale(1.05)',
              },
              '7%': { transform: 'translate(-50%, -100%) scale(1)' },
              '85%': {
                opacity: 1,
                transform: 'translate(-50%, -100%) scale(1)',
              },
              '100%': {
                opacity: 0,
                transform: 'translate(-50%, -100%) scale(1)',
              },
            },
          }}
        >
          “{text}”
        </Box>
      ))}
      <Box
        ref={tooltipRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.15s',
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          fontSize: 12,
          whiteSpace: 'nowrap',
          zIndex: 1,
        }}
      />
    </Box>
  )
}

export default BubbleField
